// OpenWeatherMap API Service
const API_KEY = process.env.REACT_APP_OPENWEATHER_API_KEY;
const BASE_URL = 'https://api.openweathermap.org/data/2.5';

// OpenUV API for UV Index
const OPENUV_API_KEY = process.env.REACT_APP_OPENUV_API_KEY;
const OPENUV_URL = 'https://api.openuv.io/api/v1/uv';

/**
 * Get UV Index from OpenUV API
 */
const getUVIndexFromOpenUV = async (lat, lon) => {
  try {
    console.log('Fetching UV Index from OpenUV:', lat, lon);
    console.log('Using OpenUV API Key:', OPENUV_API_KEY ? 'Available' : 'NOT SET!');
    
    const response = await fetch(
      `${OPENUV_URL}?lat=${lat}&lng=${lon}`,
      {
        headers: {
          'x-access-token': OPENUV_API_KEY,
          'Content-Type': 'application/json'
        }
      }
    );
    
    if (!response.ok) {
      console.log('OpenUV API error:', response.status);
      const errorData = await response.json().catch(() => ({}));
      console.error('OpenUV error details:', errorData);
      return null;
    }
    
    const data = await response.json();
    console.log('OpenUV response:', data);
    
    const uvIndex = data.result?.uv;
    
    if (uvIndex !== undefined && uvIndex !== null) {
      console.log('UV Index from OpenUV:', uvIndex);
      return Math.round(uvIndex * 10) / 10;
    }
    
    console.log('UV Index not available in response');
    return null;
  } catch (error) {
    console.error('Error fetching UV Index from OpenUV:', error);
    return null;
  }
};

/**
 * Calculate dew point from temperature and humidity
 * Magnus formula approximation
 */
const calculateDewPoint = (temp, humidity) => {
  const a = 17.27;
  const b = 237.7;
  const alpha = ((a * temp) / (b + temp)) + Math.log(humidity / 100);
  const dewPoint = (b * alpha) / (a - alpha);
  return Math.round(dewPoint * 10) / 10;
};

/**
 * Get weather data by city name
 */
export const getWeatherByCity = async (cityName) => {
  try {
    console.log('Fetching weather for city:', cityName);
    console.log('Using API Key:', API_KEY ? 'Available' : 'NOT SET!');
    
    const response = await fetch(
      `${BASE_URL}/weather?q=${cityName}&appid=${API_KEY}&units=metric&lang=id`
    );
    
    console.log('City weather API response status:', response.status);
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error('City weather API error:', errorData);
      
      if (response.status === 401) {
        throw new Error('API Key tidak valid. Pastikan API Key sudah benar dan aktif (butuh 10-30 menit setelah dibuat).');
      } else if (response.status === 404) {
        throw new Error('Kota tidak ditemukan. Coba nama lain (contoh: Jakarta, Bandung).');
      }
      throw new Error('Gagal mengambil data cuaca: ' + (errorData.message || 'Unknown error'));
    }
    
    const data = await response.json();
    console.log('City weather data received:', data);
    
    // Get UV Index from OpenUV
    let uvIndex = null;
    if (data.coord) {
      uvIndex = await getUVIndexFromOpenUV(data.coord.lat, data.coord.lon);
    }
    
    return parseWeatherData(data, uvIndex);
  } catch (error) {
    console.error('Error fetching weather by city:', error);
    throw error;
  }
};

/**
 * Get weather data by coordinates with UV Index
 */
export const getWeatherByCoordinates = async (lat, lon) => {
  try {
    console.log('Fetching weather for coordinates:', lat, lon);
    console.log('Using API Key:', API_KEY ? 'Available' : 'NOT SET!');
    
    // Get current weather data
    const weatherResponse = await fetch(
      `${BASE_URL}/weather?lat=${lat}&lon=${lon}&appid=${API_KEY}&units=metric&lang=id`
    );
    
    console.log('Weather API response status:', weatherResponse.status);
    
    if (!weatherResponse.ok) {
      const errorData = await weatherResponse.json().catch(() => ({}));
      console.error('Weather API error:', errorData);
      
      if (weatherResponse.status === 401) {
        throw new Error('API Key tidak valid. Pastikan API Key sudah benar dan aktif (butuh 10-30 menit setelah dibuat).');
      }
      throw new Error('Gagal mengambil data cuaca dari koordinat: ' + (errorData.message || 'Unknown error'));
    }
    
    const weatherData = await weatherResponse.json();
    console.log('Weather data received:', weatherData);
    
    // Get UV Index from OpenUV
    const uvIndex = await getUVIndexFromOpenUV(lat, lon);
    
    return parseWeatherData(weatherData, uvIndex);
  } catch (error) {
    console.error('Error fetching weather by coordinates:', error);
    throw error;
  }
};

/**
 * Get current location using browser Geolocation API
 */
export const getCurrentLocation = () => {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('Geolocation tidak didukung oleh browser Anda'));
      return;
    }
    
    const options = {
      enableHighAccuracy: true,
      timeout: 10000, // 10 seconds
      maximumAge: 0
    };
    
    navigator.geolocation.getCurrentPosition(
      (position) => {
        console.log('Location obtained:', position.coords.latitude, position.coords.longitude);
        resolve({
          lat: position.coords.latitude,
          lon: position.coords.longitude
        });
      },
      (error) => {
        console.error('Geolocation error:', error);
        let errorMessage = 'Gagal mendapatkan lokasi Anda';
        
        switch(error.code) {
          case error.PERMISSION_DENIED:
            errorMessage = 'Anda menolak izin akses lokasi. Klik ikon 🔒 di address bar dan izinkan akses lokasi.';
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage = 'Informasi lokasi tidak tersedia. Pastikan GPS/Location Services aktif.';
            break;
          case error.TIMEOUT:
            errorMessage = 'Timeout saat mengambil lokasi. Coba lagi atau gunakan input nama kota.';
            break;
          default:
            errorMessage = 'Error: ' + error.message;
        }
        
        reject(new Error(errorMessage));
      },
      options
    );
  });
};

/**
 * Parse weather data to CCRS parameters
 */
const parseWeatherData = (data, uvIndex = null) => {
  const temp = data.main.temp;
  const humidity = data.main.humidity;
  
  return {
    // Curah Hujan (mm) - dari 1 jam terakhir, atau 0 jika tidak ada hujan
    Rain: data.rain?.['1h'] || data.rain?.['3h'] || 0,
    
    // Wind Speed (m/s)
    Wind: Math.round(data.wind.speed * 10) / 10,
    
    // Humidity (%)
    Humidity: humidity,
    
    // Visibility (km) - convert dari meter ke km
    Visibility: Math.round((data.visibility / 1000) * 10) / 10,
    
    // Pressure (hPa)
    Pressure: data.main.pressure,
    
    // UV Index - dari One Call API jika tersedia
    UV: uvIndex !== null ? Math.round(uvIndex * 10) / 10 : '',
    
    // Dew Point (°C) - calculate dari temp & humidity
    DewPoint: calculateDewPoint(temp, humidity),
    
    // Soil Moisture & pH - tidak tersedia dari API, biarkan kosong
    SoilMoisture: '',
    pH: '',
    
    // Metadata
    location: data.name,
    country: data.sys.country,
    description: data.weather[0]?.description || '',
    icon: data.weather[0]?.icon || '',
    temp: Math.round(temp * 10) / 10
  };
};

export const weatherService = {
  getWeatherByCity,
  getWeatherByCoordinates,
  getCurrentLocation
};
